import type { Styles } from '@adornis/ass/style.js';
import type { NarrowedEntityData } from '@adornis/bh-util/selectionset-types.js';
import type { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import type { ChemistryControllerHost } from '@adornis/chemistry/controllers/chemistry-controller.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { ColorType } from '@adornis/chemistry/elements/theming/coloring-system.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { html } from 'lit';
import { getDbPluginForScope, type PaymentPlugin } from './db-plugins.js';
import type { Bundle } from './entities/Bundle.js';
import { Product } from './entities/products.js';
import type { User } from './entities/user.js';
import { Workshop } from './entities/workshop.js';
import { ParticipationState } from './enums/enums.js';

export function productPriceNumber(
  workshop: NarrowedEntityData<Product, { hasPrice: 1; prices: { _class: 1; unitAmount: 1 } }>,
): number | [number, number] {
  if (!workshop.hasPrice) return 0;
  const prices = workshop.prices
    .filter(price => price._class === 'Price')
    .map(p => p.unitAmount)
    .toSorted((a, b) => a - b);
  if (prices.length === 0) throw new Error('unreachable');
  else if (prices.length === 1 || prices.at(0) === prices.at(-1)) return prices[0] as number;
  else return [prices.at(0) as number, prices.at(-1) as number];
}

export async function getTargetPrice(targetProd: Product, curUser: User, payPlugin: PaymentPlugin<string, string>) {
  const price = await payPlugin.getProductPrice(targetProd._id, curUser._id);
  return price?.unitAmount ?? 0;
}

export function productPrice(workshop: Product) {
  const price = productPriceNumber(workshop);
  return formatPrice(price);
}

// price can also be price range
export function formatPrice(amount: number | [number, number] | null, isEmptyIndicator = '---') {
  if (amount === null) return isEmptyIndicator;

  const _formatPriceRaw = amount => (amount / 100).toFixed(2).replace(/,/g, '').replace('.', ',');
  const _formatPrice = amount =>
    Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount / 100);

  if (typeof amount === 'number') {
    if (amount < 0) throw new Error('UnitPrice is negative');
    return _formatPrice(amount);
  }

  const [amount1, amount2] = amount;
  if (amount1 < 0) throw new Error('UnitPrice is negative (amount1)');
  if (amount2 < 0) throw new Error('UnitPrice is negative (amount2)');
  if (amount1 === amount2) throw new Error('pricerange is actually only single value');
  if (amount1 > amount2) throw new Error(`range is invalid: ${amount1} - ${amount2}`);
  return _formatPriceRaw(amount1) + ' - ' + _formatPrice(amount2);
}

export const dataviewEmptyState = stateful((props: { styles?: Styles; text?: string }) => {
  const { styles, text } = props;
  const design = useObservable(() => DesignSystem.currentTheme, []);
  return html`
    <tr-flex
      w100
      padding="sm"
      center
      crossaxis-center
      ${acss({ background: design?.colors.tone.onyx, color: '#fff', ...styles })}
    >
      <tr-text> ${text ?? 'Keine Datensätze gefunden'} </tr-text>
    </tr-flex>
  `;
});

export const dataviewLoadingState = (host: ChemistryLitElement) => html`
  <tr-flex w100 padding="sm" center crossaxis-center ${css({ background: host.colors.tone.onyx, color: '#fff' })}>
    <tr-text> Datensätze werden geladen... </tr-text>
  </tr-flex>
`;

export function getParticipationStateColors(state: ParticipationState, host: ChemistryControllerHost) {
  return (
    {
      [ParticipationState.participated]: host.colors.tone.success,
      [ParticipationState.in]: host.colors.tone.light_blue,
      [ParticipationState.left]: host.colors.tone.light_orange,
      [ParticipationState.waiting]: host.colors.tone.light_bronze,
      [ParticipationState.suspended]: host.colors.tone.light_grau,
      [ParticipationState.no_show]: host.colors.tone.light_red,
    } as { [k in ParticipationState]: ColorType }
  )[state];
}

export type BookingBillDataBundle<
  BundleType extends NarrowedEntityData<Bundle, { _id: 1 }> = NarrowedEntityData<Bundle, { _id: 1 }>,
> = {
  bundle: BundleType;
  products: NarrowedEntityData<Product, { _id: 1; name: 1; prices: { _id: 1; unitAmount: 1 } }>[];
};
export type BookingBillData<BundleType extends NarrowedEntityData<Bundle, { _id: 1 }>> = {
  booked: (Product | BookingBillDataBundle<BundleType>)[];
  totalPrice: 0;
};

export function getProductsBestBundled<T extends NarrowedEntityData<Bundle, { _id: 1; productIDs: 1; isActive: 1 }>>(
  selectedProducts: NarrowedEntityData<Product, { _id: 1; name: 1; prices: { _id: 1; unitAmount: 1 } }>[],
  selectedBundles: T[],
): (Product | BookingBillDataBundle<T>)[] {
  const bundles = selectedBundles.filter(bundle => bundle.isActive);
  let singleSelectedProds = [...selectedProducts];
  let prodsGroupByBundle = new Array<BookingBillDataBundle<T>>();

  bundles.forEach(curBundle => {
    const bundleProds = new Array<
      NarrowedEntityData<Product, { _id: 1; name: 1; prices: { _id: 1; unitAmount: 1 } }>
    >();
    const leftProds = new Array<NarrowedEntityData<Product, { _id: 1; name: 1; prices: { _id: 1; unitAmount: 1 } }>>();

    singleSelectedProds.forEach(curProd => {
      if (curBundle.productIDs.includes(curProd._id)) bundleProds.push(curProd);
      else leftProds.push(curProd);
    });

    if (bundleProds.length === curBundle.productIDs.length) {
      singleSelectedProds = leftProds;
      prodsGroupByBundle.push({
        bundle: curBundle,
        products: bundleProds,
      });
    }
  });

  // TODO: add correct types
  return [...prodsGroupByBundle, ...singleSelectedProds] as any;
}

type BillDataProduct = NarrowedEntityData<
  Product,
  { _id: 1; name: 1; institutionID: 1; prices: { _id: 1; unitAmount: 1 } }
>;

// Require capacity when the caller's type retains Workshop-specific fields.
// Queries narrowed to Product erase those fields even when selected at runtime.
type BillDataProductWithCapacity<P extends BillDataProduct> = P &
  (Extract<keyof P, Exclude<keyof Workshop, keyof Product>> extends never ? unknown : Pick<Workshop, 'leftCapacity'>);

export async function getBillData<
  T extends NarrowedEntityData<
    Bundle,
    {
      _id: 1;
      productIDs: 1;
      isActive: 1;
      hasPrice: 1;
      price: { unitAmount: 1 };
      getUnitAmount: 1;
      name: 1;
      products: { _id: 1; name: 1; prices: { _id: 1; unitAmount: 1 } };
    }
  >,
  P extends BillDataProduct,
>(
  selectedProducts: BillDataProductWithCapacity<P>[],
  selectedBundles: T[],
  userID: string,
) {
  const bestFittedBooks = getProductsBestBundled<
    NarrowedEntityData<
      Bundle,
      {
        _id: 1;
        isActive: 1;
        hasPrice: 1;
        price: { unitAmount: 1 };
        productIDs: 1;
        getUnitAmount: 1;
        name: 1;
        products: { _id: 1; name: 1 };
      }
    >
  >(selectedProducts, selectedBundles);
  let totalPrice = 0;
  for (const curBook of bestFittedBooks) {
    if (curBook instanceof Product) {
      if (curBook instanceof Workshop && curBook.leftCapacity === 0) void 0; // ignore waitinglist workshops
      else {
        const paymentPlugin = getDbPluginForScope('payment', curBook.institutionID);
        const price = await paymentPlugin.getProductPrice(curBook._id, userID);
        totalPrice += price?.unitAmount ?? 0;
      }
    } else {
      const curBundle = curBook.bundle;
      if (curBundle.hasPrice) totalPrice += curBundle.price.unitAmount;
    }
  }

  return {
    booked: bestFittedBooks,
    totalPrice,
  };
}
